import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { FileDownloadButton } from '../../../../../components/FileDownloadButton/FileDownloadButton'
import { EFileStatus } from '../../../../../types/enums'
import { isVirtualMerchant } from '../../../../../utils/isVirtualMerchant'
import { MatchingFile } from '../../core/_models'
import { splitReceipts } from '../../core/receipts'
import { BillStatusBadge } from './BillStatusBadge'
import { ReceiptHistory } from './ReceiptHistory'
import { ReceiptReplaceButton } from './ReceiptReplaceButton'
import { ReceiptUploadButton } from './ReceiptUploadButton'
import { useReceiptActions } from './useReceiptActions'

interface ReceiptCellProps {
  ticketToken: string
  files: MatchingFile[] | null | undefined
  merchantId: number | string | null | undefined
  /** Прикрепление и замена (роли canEditTickets). */
  canManageBills: boolean
  /** Кнопки подтверждения/отклонения — поверхность добавляет свои гейты (открытый спор). */
  canDecide: boolean
  /** Бейдж «подтверждён/отклонён» — там, где он был: активные, завершённые, диспуты. */
  showDecisionBadge?: boolean
  /** Первое прикрепление: очередь, завершённые, диспуты; не в журнале и не у выплаты. */
  allowUpload?: boolean
  onUploaded?: (file: MatchingFile) => void
  /** files — the receipts as the cell shows them after the decision (a replacement included). */
  onDecided?: (billId: MatchingFile['id'], status: EFileStatus, files: MatchingFile[]) => void
  onDecisionAcknowledged?: () => void
  badgePosition?: 'start' | 'end'
  /** Дополнительный бейдж после файла, например ReceiptGateBadge в активной очереди. */
  extra?: ReactNode
}

/** Which receipts the server reports and which one stands — statuses aside. */
const receiptsKey = (files: MatchingFile[] | null | undefined): string =>
  (files ?? []).map((f) => `${f.id}:${f.current !== false ? 'c' : 'h'}`).join(',')

/**
 * Ячейка чека пополнения — одна на все поверхности, где его видно (очередь мэтчинга, завершённые,
 * диспуты, пополнения выплаты, журнал пополнений): текущий чек, решение по нему, замена и история.
 *
 * После замены ячейка сразу показывает ответ (локальная подмена) и сбрасывает её, только когда
 * родитель приносит другой набор чеков (опрос после замены): новая ссылка на прежние чеки —
 * решение по чеку или запоздавший опрос — подмену не сбрасывает, иначе вернулся бы заменённый файл.
 */
export const ReceiptCell = ({
  ticketToken,
  files,
  merchantId,
  canManageBills,
  canDecide,
  showDecisionBadge = false,
  allowUpload = true,
  onUploaded,
  onDecided,
  onDecisionAcknowledged,
  badgePosition = 'start',
  extra,
}: ReceiptCellProps) => {
  const [override, setOverride] = useState<MatchingFile[] | null>(null)
  const serverKey = receiptsKey(files)
  const serverKeyRef = useRef(serverKey)
  serverKeyRef.current = serverKey
  // The decision's dialog keeps the handler of the render that opened it: the receipts it hands to the
  // parent are read at the moment the decision lands.
  const shownRef = useRef<MatchingFile[]>([])
  shownRef.current = override ?? files ?? []
  useEffect(() => setOverride(null), [serverKey])

  // The list may have been reloaded while the request was in flight and already show these receipts:
  // then there is nothing to hold, and holding it would hide every later update of the same receipts.
  const onReplaced = useCallback(
    (next: MatchingFile[]) => setOverride(receiptsKey(next) === serverKeyRef.current ? null : next),
    []
  )

  const { current, history } = splitReceipts(override ?? files)
  const virtual = isVirtualMerchant(merchantId)

  const actions = useReceiptActions({
    ticketToken,
    canManageBills,
    onUploaded: useCallback((file: MatchingFile) => onUploaded?.({ ...file, current: true }), [onUploaded]),
    onDecided: useCallback(
      (billId: MatchingFile['id'], status: EFileStatus) => {
        const decide = (list: MatchingFile[]) => list.map((f) => (f.id === billId ? { ...f, status } : f))
        setOverride((prev) => (prev ? decide(prev) : prev))
        onDecided?.(billId, status, decide(shownRef.current))
      },
      [onDecided]
    ),
    onDecisionAcknowledged,
    onReplaced,
  })

  if (!current) {
    if (!allowUpload || virtual) return <span className='text-muted'>-</span>

    return (
      <ReceiptUploadButton
        fileInputRef={actions.fileInputRef}
        isUploading={actions.isUploading}
        onClick={actions.handleUploadClick}
        onFileSelect={actions.handleFileSelect}
      />
    )
  }

  return (
    <div className='d-flex align-items-center justify-content-center gap-2'>
      <FileDownloadButton
        url={current.url}
        id={current.id}
        status={current.status}
        badgePosition={badgePosition}
        badgeStatus={current.autoCheckStatus}
        badgekMessage={current.autoCheckMessage}
      />
      {showDecisionBadge && (
        <BillStatusBadge
          bill={current}
          canManageBills={canDecide}
          handleVerifyBill={() => actions.handleVerifyBill(current)}
          handleRejectBill={() => actions.handleRejectBill(current)}
        />
      )}
      {/* the extra badge speaks about the receipt the list was loaded with */}
      {override ? null : extra}
      {/* replacement and its history sit together, apart from the decision badges */}
      <span className='d-inline-flex align-items-center gap-1'>
        {canManageBills && !virtual && (
          <ReceiptReplaceButton
            inputRef={actions.replaceInputRef}
            isBusy={actions.isReplacing}
            onClick={actions.handleReplaceClick}
            onFileSelect={actions.handleReplaceSelect}
          />
        )}
        <ReceiptHistory history={history} />
      </span>
    </div>
  )
}
